import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Check, Copy, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Toggle } from "../SectionBlock";

export function Field({
	label,
	hint,
	htmlFor,
	children,
	className,
}: {
	label: string;
	hint?: ReactNode;
	htmlFor?: string;
	children: ReactNode;
	className?: string;
}) {
	return (
		<div className={className}>
			<label htmlFor={htmlFor} className="text-sm font-medium text-muted-foreground mb-1.5 block">
				{label}
			</label>
			{children}
			{hint && <p className="text-xs text-muted mt-1">{hint}</p>}
		</div>
	);
}

export function ToggleRow({
	label,
	description,
	checked,
	onChange,
	disabled,
}: {
	label: string;
	description?: string;
	checked: boolean;
	onChange: (next: boolean) => void;
	disabled?: boolean;
}) {
	return (
		<div className={cn("flex items-center justify-between gap-4 py-3", disabled && "opacity-50 pointer-events-none")}>
			<div className="min-w-0">
				<p className="text-sm font-medium text-foreground">{label}</p>
				{description && <p className="text-xs text-muted mt-0.5">{description}</p>}
			</div>
			<Toggle checked={checked} onChange={() => onChange(!checked)} />
		</div>
	);
}

export function CopyField({ value, id }: { value: string; id?: string }) {
	const [copied, setCopied] = useState(false);
	async function copy() {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			toast.error("Could not copy to clipboard.");
		}
	}
	return (
		<div className="flex gap-2">
			<Input id={id} value={value} readOnly className="font-mono text-xs" />
			<Button type="button" variant="outline" size="icon" onClick={copy} aria-label="Copy">
				{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
			</Button>
		</div>
	);
}

/**
 * Write-only secret input. Shows the saved hint, never the value.
 * value: undefined/"" = keep, string = replace, null = clear on save.
 */
export function SecretInput({
	id,
	isSet,
	hint,
	value,
	onChange,
	placeholder,
	disabled,
}: {
	id?: string;
	isSet: boolean;
	hint: string | null;
	value: string | null | undefined;
	onChange: (next: string | null) => void;
	placeholder?: string;
	disabled?: boolean;
}) {
	const clearing = value === null;
	return (
		<div className="space-y-1.5">
			<div className="flex gap-2">
				<Input
					id={id}
					type="password"
					autoComplete="off"
					value={clearing ? "" : (value ?? "")}
					onChange={(e) => onChange(e.target.value)}
					placeholder={clearing ? "Will be removed on save" : isSet ? "Enter a new value to replace" : placeholder}
					disabled={disabled || clearing}
				/>
				{isSet && (
					<Button
						type="button"
						variant={clearing ? "outline" : "destructive_outline"}
						onClick={() => onChange(clearing ? "" : null)}
						disabled={disabled}
					>
						{clearing ? "Undo" : "Remove"}
					</Button>
				)}
			</div>
			<p className="text-xs text-muted">
				{isSet ? `Saved ${hint ?? "••••"}. Stored encrypted.` : "Not set."}
			</p>
		</div>
	);
}

export function TagListInput({
	id,
	values,
	onChange,
	placeholder,
	disabled,
}: {
	id?: string;
	values: string[];
	onChange: (next: string[]) => void;
	placeholder?: string;
	disabled?: boolean;
}) {
	const [draft, setDraft] = useState("");

	function commit() {
		const items = draft
			.split(/[,\s]+/)
			.map((v) => v.trim().toLowerCase())
			.filter(Boolean);
		if (items.length) onChange([...new Set([...values, ...items])]);
		setDraft("");
	}

	function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
		if (e.key === "Enter" || e.key === ",") {
			e.preventDefault();
			commit();
		} else if (e.key === "Backspace" && !draft && values.length) {
			onChange(values.slice(0, -1));
		}
	}

	return (
		<div
			className={cn(
				"flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-[8px] border border-border-strong bg-surface px-2 py-1.5 focus-within:ring-2 focus-within:ring-primary",
				disabled && "opacity-50",
			)}
		>
			{values.map((v) => (
				<span
					key={v}
					className="inline-flex items-center gap-1 rounded-md bg-muted-subtle px-2 py-0.5 text-xs text-foreground"
				>
					{v}
					<button
						type="button"
						onClick={() => onChange(values.filter((x) => x !== v))}
						disabled={disabled}
						aria-label={`Remove ${v}`}
						className="text-muted hover:text-foreground"
					>
						<X className="h-3 w-3" />
					</button>
				</span>
			))}
			<input
				id={id}
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onKeyDown={onKeyDown}
				onBlur={commit}
				placeholder={values.length ? "" : placeholder}
				disabled={disabled}
				className="min-w-[140px] flex-1 bg-transparent text-sm text-foreground placeholder:text-muted outline-none"
			/>
		</div>
	);
}

export const textareaClass =
	"w-full rounded-[8px] border border-border-strong bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50";
